import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useRouter } from "next/router";
import { useForm, useWatch } from "react-hook-form";

import Layout from "../components/layouts/layout.comp";
import LocalePs from "../components/locale/localePs.core";
import { ListSection, Tag } from "../components/reuseables/list.comp";
import brandCore from "../core/brand.core";
import localeCore from "../core/locale.core";
import warehouseCore from "../core/warehouse.core";
import { useTranslation } from "../core/translation.core";
import ErrorCenterIcon from "../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../icons/loadingOvalCenter.icon";
import { Brand } from "../types/brand";
import { Warehouse } from "../types/warehouse";
import AddLink from "../components/form/addLink.comp";
import { BrandInventorySection } from "../components/brand/brandInventorySection.comp";
import PageNav from "../components/common/pageNav.comp";
import { DocListFilter } from "../types/product";

export default function BrandsPage() {
  const { t, msg } = useTranslation();

  return (
    <Layout title={t(msg.brand.brand)} titleBtn={<AddLink id="brand-create" href="/brand/create" />}>
      <ListSection>
        <List />
      </ListSection>
    </Layout>
  );
}

const List = () => {
  const { t, msg } = useTranslation();
  const router = useRouter();
  const methods = useFormPagination();
  const [showInventory, setShowInventory] = useState(false);
  const [pageSize] = useWatch({
    name: ["paging.size"],
    control: methods.control,
  });
  const page = Number(router.query.page) || 1;
  const size = pageSize || 10;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["brands", page, size],
    queryFn: () => brandCore.listBrands(page, size),
  });
  const { data: warehouses, isFetched: warehousesFetched } = useQuery<Warehouse[]>({
    queryKey: ["Warehouse"],
    queryFn: warehouseCore.list,
  });

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isLoading || !warehousesFetched) return <LoadingOvalCenterIcon />;

  const warehouseColumns = showInventory && warehouses ? warehouses.map(() => "minmax(120px,1fr)").join(" ") : "";
  const gridCols = `6fr 3fr 3fr 2fr${warehouseColumns ? ` ${warehouseColumns}` : ""}`;

  return (
    <div id="brand-list" className="pt-6 lg:pt-0">
      <div className="px-6 py-3">
        <button
          data-role="show-inventory"
          className="underline text-sm"
          onClick={() => setShowInventory(!showInventory)}
          type="button"
        >
          {showInventory ? t(msg.form.hide) : t(msg.brand.showInventory)}
        </button>
      </div>
      <div
        className="hidden lg:grid gap-6 px-6 py-3 text-sm font-medium"
        style={{ gridTemplateColumns: window.innerWidth >= 1024 ? gridCols : "1fr" }}
      >
        <div>{t(msg.brand.name)}</div>
        <div>{t(msg.category.code)}</div>
        {showInventory && warehouses?.map((warehouse) => <div key={warehouse.id}>{warehouse.name}</div>)}
        <div></div>
      </div>

      {data?.items.map((a) => (
        <Row key={a.id} brand={a} warehouses={showInventory ? warehouses : undefined} />
      ))}
      <PageNav page={page} size={size} total={data?.total ?? 0} pathname="/brands" query={router.query} />
    </div>
  );
};

function useFormPagination() {
  const methods = useForm<DocListFilter>();
  const { query, isReady } = useRouter();

  useEffect(() => {
    methods.setValue("paging.page", Number(query.page ?? 1));

    // eslint-disable-next-line
  }, [query, isReady]);

  return methods;
}

function Row({ brand, warehouses }: { brand: Brand; warehouses?: Warehouse[] }) {
  const { t, msg } = useTranslation();

  const warehouseColumns = warehouses ? warehouses.map(() => "minmax(120px,1fr)").join(" ") : "";
  const gridCols = `6fr 3fr 3fr 2fr${warehouseColumns ? ` ${warehouseColumns}` : ""}`;

  return (
    <div
      className="brand-row grid grid-cols-2 gap-x-6 gap-y-3 px-6 py-6 border-t-[1px] text-sm group/edit"
      style={{ gridTemplateColumns: window.innerWidth >= 1024 ? gridCols : "1fr" }}
    >
      <div data-value={localeCore.getLocaleValueDefault(brand.name)}>
        <Tag>{t(msg.brand.name)}</Tag>
        <LocalePs value={brand.name} />
      </div>
      <div data-value={localeCore.getLocaleValueDefault(brand.name)}>
        <Tag>{t(msg.brand.name)}</Tag>
        <p>{brand.code}</p>
      </div>
      {warehouses?.map((warehouse) => (
        <BrandInventorySection key={warehouse.id} brandId={brand.id} warehouseId={warehouse.id} />
      ))}
      <div className="col-span-2 lg:col-span-1 opacity-0 group-hover/edit:opacity-100">
        <Link className="brand-edit underline" href={`/brand/edit/${brand.id}`}>
          {t(msg.form.edit)}
        </Link>
      </div>
    </div>
  );
}
